import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import {
	createChart,
	ColorType,
	LineStyle,
	LineType,
	CandlestickSeries,
	HistogramSeries,
	LineSeries,
} from "lightweight-charts";
import type { IChartApi, ISeriesApi, IPriceLine, MouseEventParams, Time, UTCTimestamp } from "lightweight-charts";
import { Alert, Button, Skeleton, Tooltip } from "@heroui/react";
import { Maximize2, Minimize2, PenLine, Waypoints } from "lucide-react";
import { Panel } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { Timeframe, TechnicalIndicators } from "@/types";
import type { ChartLevel } from "@/components/daytrader/dayTrader";
import { useActiveTheme } from "@/lib/theme";
import { fmt, pricePrecision } from "../desk/deskData";
import { SETUP_ID, SignalOverlay, type OverlayBar, type Rect } from "./SignalOverlay";
import { SetupCard, SetupLegendRow, SignalCard, SignalLegend } from "./SignalLegend";
import { summarize, useChartSignals, utcStamp, withDeskCheck, type ChartSignal, type DeskCheck, type SetupZone } from "./chartSignals";
import { emaSeries, freshBars, pacedVolume, priorVolumeAverage, rsiSeries, stepToBars, type Candle } from "./indicators";
import { BandPrimitive } from "./BandPrimitive";
import { StructurePrimitive } from "./StructurePrimitive";
import { BiasLegendRow, StructureLegendRow } from "./BiasLegend";
import { readStructure, swings as structureSwings, trendChannel, type StructureRead } from "./marketStructure";
import { biasCheck, biasEma200Series, srLevels, type BiasCheck } from "./structure";
import { DrawingManager } from "./drawing/DrawingManager";
import { DrawingRail, placingHint } from "./drawing/DrawingRail";
import { DrawingStyleBar } from "./drawing/DrawingStyleBar";
import { drawingStore, useDrawings } from "./drawing/drawingStore";
import type { DrawingTool } from "./drawing/types";
import { ReplayBar } from "./replay/ReplayBar";
import { IndicatorsMenu, TimeframeTabs, ToolbarIcon, type IndicatorGroup } from "./ChartToolbar";
import { LiveActionBar } from "./ActionBar";
import {
	addToRecord, alignToBar, atr, drawnBy, practiceR, practiceSignal, randomReplayTime, readRecord, replayAfterBars,
	REPLAY_BEFORE_BARS, scorecard, settle, signalAt as replaySignalAt, signalWindow, stepTime, visibleAt,
	type PracticeRecord, type PracticeTrade, type ReplaySpeed,
} from "./replay/replay";
import { themeColor } from "./themeColor";

interface PriceChartProps {
	symbol: string;
	timeframe: Timeframe;
	indicators?: TechnicalIndicators;
	/** Optional horizontal reference levels (e.g. intraday VWAP / prior-day H/L). */
	levels?: ChartLevel[];
	/** Right-aligned panel header slot (timeframe control, toggles). */
	actions?: ReactNode;
	/** Chart canvas height in px (default 420). */
	height?: number;
	/** Show the full-screen button (default true). Off inside drawers/modals,
	 *  where a fixed overlay would be trapped by the drawer's transform. */
	allowExpand?: boolean;
	/** Full screen, when the page owns it (the desk keeps it in the address bar); otherwise the chart's own. */
	expanded?: boolean;
	onExpandedChange?: (on: boolean) => void;
	/** Draw the 1h strategy's signals: BUY/SELL tags, stop/target boxes, outcomes. */
	showSignals?: boolean;
	/** The desk's answer for the candle it last checked; it wins on that candle. */
	deskCheck?: DeskCheck | null;
	/** What the coin is waiting for (desk stage Waiting / Almost), drawn with the signals. */
	setup?: SetupZone | null;
	/** The user's own logged trades on this coin ("YOU BUY"), drawn even with signals off. */
	myTrades?: ChartSignal[];
	/** Dollars risked per trade (day-risk settings): a drawn Buy/Sell plan shows the size it buys. */
	riskUsd?: number;
	/** Timeframe tabs in the chart's toolbar (Binance's "15m 1h 4h"). */
	timeframes?: Timeframe[];
	onTimeframe?: (tf: Timeframe) => void;
	/** The Indicators menu's Signals / Levels chips (the desk owns both switches). */
	onShowSignals?: (on: boolean) => void;
	showLevels?: boolean;
	onShowLevels?: (on: boolean) => void;
	/** In place of the coin's name in the toolbar: e.g. a coin picker ("SOL/USDT ▾"). */
	symbolControl?: ReactNode;
	/** Big Buy / Sell under the chart; a replay swaps in its practice ones. */
	onBuy?: () => void;
	onSell?: () => void;
	className?: string;
}

/** Bars in view on first load: about a day of 15m, half a day of 5m, 5 days of 1h. */
const INITIAL_BARS: Record<string, number> = { "1m": 180, "5m": 144, "15m": 96, "1h": 120, "4h": 90, "1d": 120 };
const TF_SEC: Record<string, number> = { "1m": 60, "5m": 300, "15m": 900, "1h": 3600, "4h": 14400, "1d": 86400 };
/** How often the forming candle refreshes. */
const LIVE_POLL_MS = 10_000;
const LEVELS_PREF = "vp.chart.levels";
const RSI_PREF = "vp.chart.rsi";
const DRAW_PREF = "vp.chart.draw";
const MAGNET_PREF = "vp.chart.magnet";
const HIDE_DRAWINGS_PREF = "vp.chart.hideDrawings";
const EMA_PREF = "vp.chart.ema";
const VOL_PREF = "vp.chart.vol";
const SR_PREF = "vp.chart.sr";
const BIAS_PREF = "vp.chart.bias";
const STRUCTURE_PREF = "vp.chart.structure";
/** Below this the chart is too short for the drawing rail (the Simple desk drawer). */
const DRAW_MIN_H = 320;
/** The RSI pane under the price (px). */
const RSI_PANE_H = 84;
const RSI_PERIOD = 14;
/** The engine's volume yardstick: the 20 bars before the one it grades. */
const VOL_AVG_N = 20;
/** The pullback checklist's RSI "reset" band: 35–60 for buys, 40–65 for sells (backend setupClassifier). */
function rsiBandOf(direction: "LONG" | "SHORT" | undefined): { low: number; high: number } {
	return direction === "SHORT" ? { low: 40, high: 65 } : { low: 35, high: 60 };
}
/** Which structure layers draw: S/R and the bias's swings on 4h; the bias's tags give way to the structure's own. */
function structureLayers(h4: boolean, v: { sr: boolean; bias: boolean; structure: boolean }) {
	return { sr: h4 && v.sr, swings: h4 && v.bias && !v.structure, market: v.structure };
}
/** The legend's numbers: the last closed bar (what the checklist graded) and the forming one. */
interface Readout { rsiClosed: number | null; rsiNow: number | null; volClosed: number | null; volPace: number | null }

const NO_TRADES: ChartSignal[] = [];

/** An on/off chart setting remembered in this browser. */
/** Phone-width screen (under 640px): the chart trims its axis labels and tools. */
function usePhoneWidth(): boolean {
	const query = "(max-width: 639px)";
	const [phone, setPhone] = useState(() => window.matchMedia(query).matches);
	useEffect(() => {
		const mq = window.matchMedia(query);
		const onChange = () => setPhone(mq.matches);
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, []);
	return phone;
}

function useRememberedToggle(key: string, initial: boolean): [boolean, (on: boolean) => void] {
	const [on, setOn] = useState<boolean>(() => {
		try {
			const v = localStorage.getItem(key);
			return v ? v === "on" : initial;
		} catch { return initial; }
	});
	const set = (next: boolean) => {
		setOn(next);
		try { localStorage.setItem(key, next ? "on" : "off"); } catch { /* ignore */ }
	};
	return [on, set];
}

export function PriceChart({
	symbol, timeframe, levels, actions, height = 420, className, allowExpand = true, expanded: expandedProp, onExpandedChange,
	showSignals = false, deskCheck, setup,
	myTrades = NO_TRADES, riskUsd = 0, timeframes, onTimeframe, onShowSignals, showLevels, onShowLevels, symbolControl, onBuy, onSell,
}: PriceChartProps) {
	const theme = useActiveTheme();
	// Expanded = the chart covers the window (Kraken Pro / Coinbase maximise).
	// An in-app overlay, not the Fullscreen API: HeroUI popovers portal to
	// <body> and would be hidden behind a native fullscreen element.
	const [ownExpanded, setOwnExpanded] = useState(false);
	const expanded = allowExpand && (expandedProp ?? ownExpanded);
	const setExpanded = useCallback(
		(on: boolean) => (onExpandedChange ? onExpandedChange(on) : setOwnExpanded(on)),
		[onExpandedChange],
	);

	useEffect(() => {
		if (!expanded) return;
		// The drawing tools use an Escape first (cancel a tool, drop a selection).
		const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !e.defaultPrevented) setExpanded(false); };
		const prevOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		window.addEventListener("keydown", onKey);
		return () => {
			document.body.style.overflow = prevOverflow;
			window.removeEventListener("keydown", onKey);
		};
	}, [expanded, setExpanded]);
	const chartContainerRef = useRef<HTMLDivElement>(null);
	const chartRef = useRef<IChartApi | null>(null);
	const candleSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
	const priceLinesRef = useRef<IPriceLine[]>([]);
	const overlayRef = useRef<SignalOverlay | null>(null);
	const rsiBandRef = useRef<BandPrimitive | null>(null);
	const rsiBandColors = useRef<{ fill: string; edge: string } | null>(null);
	// The setup's side, readable inside the chart effect without rebuilding the chart.
	const setupDirRef = useRef<"LONG" | "SHORT" | undefined>(setup?.direction);
	setupDirRef.current = setup?.direction;
	// A coin with a plan opens with room on the right for its long / short box.
	const hasPlanRef = useRef(false);
	hasPlanRef.current = !!setup?.plan;
	// RSI pane: on by default where the chart is tall enough for it; remembered.
	const [showRsi, setShowRsi] = useState<boolean>(() => {
		try {
			const v = localStorage.getItem(RSI_PREF);
			return v ? v === "on" : height >= 360;
		} catch { return height >= 360; }
	});
	const toggleRsi = () => {
		setShowRsi(v => {
			try { localStorage.setItem(RSI_PREF, v ? "off" : "on"); } catch { /* ignore */ }
			return !v;
		});
	};
	const [readout, setReadout] = useState<Readout | null>(null);
	// Indicators menu: the EMA lines and the volume bars can be hidden too.
	const [showEma, setShowEma] = useRememberedToggle(EMA_PREF, true);
	const [showVol, setShowVol] = useRememberedToggle(VOL_PREF, true);
	// 4h only: support / resistance, and the desk's 4h bias with what it reads.
	const [showSr, setShowSr] = useRememberedToggle(SR_PREF, true);
	const [showBias, setShowBias] = useRememberedToggle(BIAS_PREF, true);
	const [bias, setBias] = useState<BiasCheck | null>(null);
	// Any timeframe, from the toolbar: the swings (HH / HL / LH / LL), the trend they make and its channel.
	const [showStructure, setShowStructure] = useRememberedToggle(STRUCTURE_PREF, false);
	const [structureRead, setStructureRead] = useState<StructureRead | null>(null);
	const visRef = useRef({ ema: showEma, vol: showVol, sr: showSr, bias: showBias, structure: showStructure });
	visRef.current = { ema: showEma, vol: showVol, sr: showSr, bias: showBias, structure: showStructure };
	const seriesRef = useRef<{ lines: ISeriesApi<"Line">[]; vol: (ISeriesApi<"Histogram"> | ISeriesApi<"Line">)[] } | null>(null);
	const structRef = useRef<{ prim: StructurePrimitive; ema200: ISeriesApi<"Line"> | null } | null>(null);
	// The legend's numbers follow the crosshair without re-rendering React.
	const legendEls = useRef<Partial<Record<"time" | "o" | "h" | "l" | "c" | "chg" | "ema20" | "ema50" | "ema200", HTMLSpanElement | null>>>({});
	// A short note after a toggle (Binance's "Drawings hidden"), with Undo after a delete.
	const [toast, setToast] = useState<{ text: string; undo?: () => void; n: number } | null>(null);
	const showToast = (text: string, undo?: () => void) => setToast({ text, undo, n: Date.now() });
	useEffect(() => {
		if (!toast) return;
		const id = setTimeout(() => setToast(null), toast.undo ? 5000 : 2600);
		return () => clearTimeout(id);
	}, [toast]);
	/** Px from the chart's top to the RSI pane, where its label sits; null until measured. */
	const [rsiTop, setRsiTop] = useState<number | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	// Candles as loaded, tagged with their chart: signals are placed against them.
	const [bars, setBars] = useState<{ key: string; data: OverlayBar[]; lastClose: number | null } | null>(null);
	// Hovered signal (x where the pointer found it: its card opens on the other
	// side) and the selected one (clicked or stepped to: the price scale fits it).
	const [hover, setHover] = useState<{ id: string; x: number } | null>(null);
	const [selected, setSelected] = useState<{ id: string; x: number | null } | null>(null);

	// ── Drawing tools (trend lines, Fibonacci, boxes, notes) ───────────────
	// Saved per coin in this browser and shared by every chart of the coin.
	const { drawings, canUndo, canRedo } = useDrawings(symbol);
	const [drawTool, setDrawTool] = useState<DrawingTool>("none");
	const [selectedDrawingIds, setSelectedDrawingIds] = useState<string[]>([]);
	// A text note to focus (just placed, or double-clicked).
	const [editRequest, setEditRequest] = useState<{ id: string; n: number } | null>(null);
	const [railPref, setRailPref] = useRememberedToggle(DRAW_PREF, true);
	const [magnet, setMagnet] = useRememberedToggle(MAGNET_PREF, false);
	const [drawingsHidden, setDrawingsHidden] = useRememberedToggle(HIDE_DRAWINGS_PREF, false);
	const canDraw = expanded || height >= DRAW_MIN_H;
	// Phones: the chart needs every pixel, so the price axis drops its line
	// names (the full screen keeps them) and the drawing tools stay closed
	// until Draw is pressed (not remembered: the desktop choice stays as it is).
	const phone = usePhoneWidth();
	const compactAxis = phone && !expanded;
	const [phoneRail, setPhoneRail] = useState(false);
	const railOn = canDraw && (compactAxis ? phoneRail : railPref);
	const setRailOn = (on: boolean) => (compactAxis ? setPhoneRail(on) : setRailPref(on));
	/** Arm a tool; drawing while drawings are hidden shows them again. */
	const armTool = (t: DrawingTool) => {
		if (t !== "none" && drawingsHidden) setDrawingsHidden(false);
		setDrawTool(t);
	};
	const managerRef = useRef<DrawingManager | null>(null);
	// The chart, its rail and its style bar: clicks inside make this chart the
	// one the drawing keyboard shortcuts act on.
	const drawScopeRef = useRef<HTMLDivElement>(null);
	// What a rebuilt chart's drawing layer starts from.
	const drawInit = useRef({ shownDrawings: [] as typeof drawings, tool: drawTool, magnet, riskUsd, armTool });

	// ── Bar replay: rewind to a past moment, then step forward candle by candle ──
	// "picking": choosing the start. "active": the chart shows only candles that
	// had closed by `nowSec`; `anchorSec` is the time the loaded window is built
	// around (a new start, or "load the next candles"); `signals` is the engine-signal
	// window, fixed when the window loads (tied to the clock it would refetch every hour).
	type SignalWindow = { end: number; days: number };
	const [replay, setReplay] = useState<
		{ mode: "picking" } | { mode: "active"; anchorSec: number; nowSec: number; signals: SignalWindow } | null
	>(null);
	const [replayPlaying, setReplayPlaying] = useState(false);
	const [replaySpeed, setReplaySpeed] = useState<ReplaySpeed>(1);
	// The loaded window's last candle close, and whether that is the present.
	const [replayEnd, setReplayEnd] = useState<{ sec: number; today: boolean } | null>(null);
	const replayEndRef = useRef(replayEnd);
	replayEndRef.current = replayEnd;
	const [practice, setPractice] = useState<PracticeTrade[]>([]);
	const [practiceRecord, setPracticeRecord] = useState<PracticeRecord>(readRecord);
	const replayRef = useRef(replay);
	replayRef.current = replay;
	const replayActive = replay?.mode === "active";
	const replayNow = replay?.mode === "active" ? replay.nowSec : null;
	const replayAnchor = replay?.mode === "active" ? replay.anchorSec : null;
	const tfSecNow = TF_SEC[timeframe] ?? 3600;
	// The chart effect's replay hooks: show the candles closed by a time; read them.
	const replayCtlRef = useRef<{ reveal: (nowSec: number) => void; candles: () => Candle[] } | null>(null);
	/** Start a replay with `nowSec` as the replay time (the last candle shown closes then). */
	const startReplay = (nowSec: number) => {
		setReplayPlaying(false);
		setReplayEnd(null);
		setPractice([]);
		setSelected(null);
		setReplay({ mode: "active", anchorSec: nowSec, nowSec, signals: signalWindow(nowSec, Date.now() / 1000) });
	};
	const exitReplay = () => {
		setReplayPlaying(false);
		setReplayEnd(null);
		setPractice([]);
		setReplay(null);
	};
	/** One candle on, while there are loaded candles left. */
	const advance = (r: typeof replay) => {
		if (r?.mode !== "active") return r;
		const end = replayEndRef.current;
		return end && r.nowSec < end.sec ? { ...r, nowSec: stepTime(r.nowSec, tfSecNow) } : r;
	};
	// Choosing the start: a click on a candle makes it the last one shown.
	const pickRef = useRef<((barTime: number) => void) | null>(null);
	pickRef.current = replay?.mode === "picking" ? (barTime: number) => startReplay(barTime + tfSecNow) : null;
	// What the drawing layer shows: none when hidden; in a replay, none drawn on later candles.
	const shownDrawings = useMemo(
		() => (drawingsHidden ? [] : replayNow === null ? drawings : drawings.filter(d => drawnBy(d, replayNow))),
		[drawings, drawingsHidden, replayNow],
	);
	drawInit.current = { shownDrawings, tool: drawTool, magnet, riskUsd, armTool };
	const signalQuery = useChartSignals(
		symbol, timeframe, showSignals,
		replay?.mode === "active" ? replay.signals : null,
	);

	useEffect(() => {
		if (!chartContainerRef.current) return;

		setLoading(true);
		setError(null);
		setHover(null);
		setReadout(null);
		setBias(null);
		setStructureRead(null);
		// Replay: this chart shows a past window instead of live candles.
		const rp = replayRef.current?.mode === "active" ? replayRef.current : null;
		if (rp) setReplayPlaying(false);

		// Theme tokens from src/index.css, resolved to canvas-safe rgba() strings.
		// Green/red belong to candles and P&L only; every line has its own hue.
		const chartColors = {
			background: "transparent",
			textColor: themeColor("--muted", "rgba(160, 165, 180, 0.9)"),
			gridColor: themeColor("--chart-grid", "rgba(255, 255, 255, 0.04)"),
			crosshairColor: themeColor("--muted", "rgba(255, 255, 255, 0.25)", 0.45),
			crosshairLabelBg: themeColor("--surface-tertiary", "#2a2d38"),
			upColor: themeColor("--profit", "#22c55e"),
			downColor: themeColor("--loss", "#ef4444"),
			volumeUp: themeColor("--profit", "#22c55e", 0.28),
			volumeDown: themeColor("--loss", "#ef4444", 0.28),
			emaFast: themeColor("--chart-ema-fast", "#38BDF8"),
			emaSlow: themeColor("--chart-ema-slow", "#A78BFA"),
			emaLong: themeColor("--chart-ema-long", "#A8A29E"),
			foreground: themeColor("--foreground", "#f2f2f2"),
			surface: themeColor("--surface", "#1c1c1c", 1),
			rsi: themeColor("--warning", "#F59E0B"),
			rsiBand: themeColor("--warning", "#F59E0B", 0.1),
			rsiEdge: themeColor("--warning", "#F59E0B", 0.35),
			volumeAvg: themeColor("--foreground", "#f2f2f2", 0.5),
			// The line between the price and RSI panes: the theme's border, not the library's navy.
			separator: themeColor("--border", "rgba(128, 128, 128, 0.3)"),
			separatorHover: themeColor("--muted", "rgba(160, 165, 180, 0.9)", 0.25),
		};
		const font = getComputedStyle(document.documentElement).getPropertyValue("--font-mono").trim() || "ui-monospace, monospace";

		const chart = createChart(chartContainerRef.current, {
			layout: {
				background: { type: ColorType.Solid, color: chartColors.background },
				textColor: chartColors.textColor,
				fontSize: 10,
				fontFamily: font,
				panes: { separatorColor: chartColors.separator, separatorHoverColor: chartColors.separatorHover },
			},
			grid: {
				vertLines: { color: chartColors.gridColor },
				horzLines: { color: chartColors.gridColor },
			},
			crosshair: {
				mode: 0,
				vertLine: {
					width: 1,
					color: chartColors.crosshairColor,
					style: LineStyle.LargeDashed,
					labelBackgroundColor: chartColors.crosshairLabelBg,
				},
				horzLine: {
					width: 1,
					color: chartColors.crosshairColor,
					style: LineStyle.LargeDashed,
					labelBackgroundColor: chartColors.crosshairLabelBg,
				},
			},
			rightPriceScale: {
				borderVisible: false,
				// The bottom margin leaves the lowest 15% to the volume bars.
				scaleMargins: { top: 0.12, bottom: 0.2 },
			},
			// Hours on the axis (the date only where a day starts), not the same date repeated.
			// Resizing (full screen, panels) keeps the same candles in view, just bigger:
			// by default the chart would keep the candle width and add older ones.
			timeScale: { borderVisible: false, timeVisible: true, secondsVisible: false, lockVisibleTimeRangeOnResize: true },
			handleScroll: true,
			handleScale: true,
		});

		chartRef.current = chart;

		// Volume first so the candles draw over it, on its own scale in the bottom 15%.
		const volumeSeries = chart.addSeries(HistogramSeries, {
			priceScaleId: "volume",
			priceFormat: { type: "volume" },
			priceLineVisible: false,
			lastValueVisible: false,
		});
		chart.priceScale("volume").applyOptions({ scaleMargins: { top: 0.85, bottom: 0 } });

		const candleSeries = chart.addSeries(CandlestickSeries, {
			upColor: chartColors.upColor,
			downColor: chartColors.downColor,
			borderVisible: false,
			wickUpColor: chartColors.upColor,
			wickDownColor: chartColors.downColor,
		});
		candleSeriesRef.current = candleSeries;

		// Structure, attached before the signals so their tags paint over it: on
		// 4h support / resistance and the bias's swings, on any timeframe the
		// market structure when its button is on.
		const h4 = timeframe === "4h";
		const structure = new StructurePrimitive(
			{
				ink: chartColors.foreground,
				surface: chartColors.surface,
				up: chartColors.upColor,
				down: chartColors.downColor,
				muted: chartColors.textColor,
				font,
			},
			fmt,
		);
		structure.setVisible(structureLayers(h4, visRef.current));
		candleSeries.attachPrimitive(structure);

		const overlay = new SignalOverlay(
			{
				profit: chartColors.upColor,
				loss: chartColors.downColor,
				text: chartColors.foreground,
				muted: chartColors.textColor,
				surface: chartColors.surface,
				font,
			},
			fmt,
		);
		candleSeries.attachPrimitive(overlay);
		overlayRef.current = overlay;

		// The trader's own drawings, attached after the signals so they paint on
		// top. Built with the chart: a symbol / timeframe / theme switch rebuilds both.
		const manager = new DrawingManager({
			chart,
			series: candleSeries,
			container: chartContainerRef.current,
			keyScope: drawScopeRef.current ?? undefined,
			intervalSec: TF_SEC[timeframe] ?? 3600,
			theme: {
				surface: chartColors.surface,
				text: chartColors.foreground,
				muted: chartColors.textColor,
				profit: chartColors.upColor,
				loss: chartColors.downColor,
				font,
			},
			callbacks: {
				onChange: change => drawingStore.change(symbol, change),
				onToolFinished: () => setDrawTool("none"),
				onSelectionChange: setSelectedDrawingIds,
				onRequestEdit: id => setEditRequest({ id, n: Date.now() }),
				onSelectTool: t => drawInit.current.armTool(t),
				onUndo: () => drawingStore.undo(symbol),
				onRedo: () => drawingStore.redo(symbol),
			},
		});
		const init = drawInit.current;
		manager.setDrawings(init.shownDrawings);
		manager.setTool(init.tool);
		manager.setMagnet(init.magnet);
		manager.setRiskUsd(init.riskUsd);
		managerRef.current = manager;

		// A signal under the pointer: the overlay's hit test names it.
		const signalAt = (p: MouseEventParams<Time>) => (typeof p.hoveredObjectId === "string" ? p.hoveredObjectId : null);
		chart.subscribeCrosshairMove(p => {
			const id = signalAt(p);
			setHover(prev => (prev?.id === id ? prev : id ? { id, x: p.point?.x ?? 0 } : null));
			hoverIdx = typeof p.time === "number" ? indexAt(p.time) : null;
			legendAt(hoverIdx ?? candles.length - 1);
		});
		// Click a signal to select it; click empty chart to clear.
		chart.subscribeClick(p => {
			if (pickRef.current && typeof p.time === "number") {
				pickRef.current(p.time);
				return;
			}
			const id = signalAt(p);
			setSelected(id ? { id, x: p.point?.x ?? null } : null);
		});

		// The engine reads the 1h EMA 20/50. Below 1h they come from 1h candles and
		// step once per hour (the value at the last 1h close), so on every
		// timeframe the lines are the ones the signals use.
		const tfSec = TF_SEC[timeframe] ?? 3600;
		const fromHourly = tfSec < 3600;
		const emaOptions = {
			lineWidth: 2 as const,
			priceLineVisible: false,
			crosshairMarkerVisible: false,
			lineType: fromHourly ? LineType.WithSteps : LineType.Simple,
		};
		const ema20Series = chart.addSeries(LineSeries, { ...emaOptions, color: chartColors.emaFast, title: "EMA 20" });
		const ema50Series = chart.addSeries(LineSeries, { ...emaOptions, color: chartColors.emaSlow, title: "EMA 50" });

		// The engine's volume yardstick on the volume scale: the mean of the 20
		// bars before each bar, so a bar reads against it (trigger ≥ 0.5×).
		const volumeAvgSeries = chart.addSeries(LineSeries, {
			priceScaleId: "volume",
			color: chartColors.volumeAvg,
			lineWidth: 1,
			priceLineVisible: false,
			lastValueVisible: false,
			crosshairMarkerVisible: false,
		});

		// 4h: the EMA 200 the bias reads (its own 259-candle version). It can sit
		// far from the price, so it doesn't stretch the scale: off-screen, the
		// legend still gives its value.
		const ema200Series = h4
			? chart.addSeries(LineSeries, {
				...emaOptions,
				color: chartColors.emaLong,
				title: "EMA 200",
				visible: visRef.current.bias,
				autoscaleInfoProvider: () => null,
			})
			: null;
		structRef.current = { prim: structure, ema200: ema200Series };

		seriesRef.current = { lines: [ema20Series, ema50Series], vol: [volumeSeries, volumeAvgSeries] };
		for (const s of seriesRef.current.lines) s.applyOptions({ visible: visRef.current.ema });
		for (const s of seriesRef.current.vol) s.applyOptions({ visible: visRef.current.vol });

		// RSI 14 in its own pane under the price, with the checklist's "reset"
		// band shaded. Same candles as the EMAs, so it is the engine's number.
		let rsiLine: ISeriesApi<"Line"> | null = null;
		rsiBandColors.current = { fill: chartColors.rsiBand, edge: chartColors.rsiEdge };
		if (showRsi) {
			rsiLine = chart.addSeries(LineSeries, {
				color: chartColors.rsi,
				lineWidth: 1,
				priceLineVisible: false,
				crosshairMarkerVisible: false,
				lastValueVisible: true, // the pane's own label names it; the axis shows the value
				priceFormat: { type: "price", precision: 0, minMove: 1 },
				autoscaleInfoProvider: () => ({ priceRange: { minValue: 0, maxValue: 100 } }),
				lineType: fromHourly ? LineType.WithSteps : LineType.Simple,
			}, 1);
			// The band is the 1h checklist's: not shaded on 4h / 1d RSI.
			if (tfSec <= 3600) {
				const band = new BandPrimitive({ ...rsiBandOf(setupDirRef.current), ...rsiBandColors.current });
				rsiLine.attachPrimitive(band);
				rsiBandRef.current = band;
			}
			chart.panes()[1]?.setHeight(RSI_PANE_H);
			chart.priceScale("right", 1).applyOptions({ scaleMargins: { top: 0.08, bottom: 0.08 } });
		}

		// The RSI pane's top edge (px from the chart's top), for its own HTML
		// label. Re-measured whenever the pane changes size: a window resize, or
		// the trader dragging the separator between the panes.
		let rsiPaneObserver: ResizeObserver | null = null;
		const measureRsiPane = () => {
			const el = chart.panes()[1]?.getHTMLElement();
			const box = chartContainerRef.current;
			if (!el || !box) return;
			const top = Math.round(el.getBoundingClientRect().top - box.getBoundingClientRect().top);
			setRsiTop(prev => (prev === top ? prev : top));
		};
		// The pane's element exists once the chart has drawn its first frame.
		const rsiFrame = showRsi
			? requestAnimationFrame(() => {
				measureRsiPane();
				const el = chart.panes()[1]?.getHTMLElement();
				if (el) {
					rsiPaneObserver = new ResizeObserver(measureRsiPane);
					rsiPaneObserver.observe(el);
				}
			})
			: 0;

		let candles: Candle[] = [];
		let hourBars: Candle[] = []; // 1h candles behind the EMAs below 1h
		let hourFetched = 0;         // the hour they were fetched in
		let replayClock: number | null = null; // replay time; null = live
		// The legend's candle: the one under the crosshair, else the last.
		let ema20Vals: (number | null)[] = [];
		let ema50Vals: (number | null)[] = [];
		let ema200Vals: (number | null)[] = [];
		let hoverIdx: number | null = null;
		const indexAt = (t: number) => {
			let lo = 0, hi = candles.length - 1;
			while (lo <= hi) {
				const mid = (lo + hi) >> 1;
				const mt = candles[mid]!.time;
				if (mt === t) return mid;
				if (mt < t) lo = mid + 1; else hi = mid - 1;
			}
			return null;
		};
		/** Fill the OHLC / EMA line for candle i (DOM text only: no React render per mouse move). */
		const legendAt = (i: number) => {
			const el = legendEls.current;
			const c = candles[i];
			if (!c) return;
			const set = (node: HTMLSpanElement | null | undefined, text: string, tone?: string) => {
				if (!node) return;
				node.textContent = text;
				if (tone) node.dataset.tone = tone;
			};
			const prev = candles[i - 1]?.close;
			const chg = prev ? ((c.close - prev) / prev) * 100 : 0;
			set(el.time, utcStamp(c.time).replace(" UTC", ""));
			set(el.o, fmt(c.open));
			set(el.h, fmt(c.high));
			set(el.l, fmt(c.low));
			set(el.c, fmt(c.close), c.close >= c.open ? "up" : "down");
			set(el.chg, `${chg >= 0 ? "+" : "−"}${Math.abs(chg).toFixed(2)}%`, chg >= 0 ? "up" : "down");
			set(el.ema20, ema20Vals[i] != null ? fmt(ema20Vals[i]) : "—");
			set(el.ema50, ema50Vals[i] != null ? fmt(ema50Vals[i]) : "—");
			// The forming 4h candle has no bias value yet: show the last closed one's.
			const e200 = ema200Vals[Math.min(i, ema200Vals.length - 1)];
			set(el.ema200, e200 != null ? fmt(e200) : "—");
		};
		let cancelled = false;
		let polling = false;

		const hourNow = () => Math.floor(Date.now() / 3_600_000);
		const fetchCandles = async (tf: string): Promise<Candle[]> => {
			const res = await fetch(`/api/candles/${symbol}/${tf}`);
			const json = await res.json();
			if (!json.success || !Array.isArray(json.data)) throw new Error(json.error || "No candles");
			return json.data;
		};
		const toBar = (c: Candle) => ({ ...c, time: c.time as UTCTimestamp });

		/** EMA lines and volume bars for the candles on the chart. */
		const drawIndicators = () => {
			const times = candles.map(c => c.time);
			let ema20: (number | null)[];
			let ema50: (number | null)[];
			if (fromHourly) {
				const hourTimes = hourBars.map(b => b.time);
				const hourCloses = hourBars.map(b => b.close);
				ema20 = stepToBars(times, hourTimes, emaSeries(hourCloses, 20), 3600);
				ema50 = stepToBars(times, hourTimes, emaSeries(hourCloses, 50), 3600);
			} else {
				const closes = candles.map(c => c.close);
				ema20 = emaSeries(closes, 20);
				ema50 = emaSeries(closes, 50);
			}
			// Time-only points where there's no value yet keep the lines aligned with the candles.
			const line = (values: (number | null)[]) =>
				times.map((t, i) => {
					const v = values[i];
					return v == null ? { time: t as UTCTimestamp } : { time: t as UTCTimestamp, value: v };
				});
			ema20Series.setData(line(ema20));
			ema50Series.setData(line(ema50));
			ema20Vals = ema20;
			ema50Vals = ema50;
			// Structure from closed candles only (the engine drops the forming one too).
			const lastBar = candles.at(-1);
			const open = lastBar !== undefined && lastBar.time + tfSec > (replayClock ?? Date.now() / 1000);
			const closed = open ? candles.slice(0, -1) : candles;
			const zz = structureSwings(closed);
			const read = closed.length > 0 ? readStructure(zz, closed.at(-1)!.close) : null;
			const check = h4 ? biasCheck(closed) : null;
			structure.setData({
				levels: h4 && lastBar ? srLevels(closed, lastBar.close) : [],
				swings: check ? check.swings : null,
				market: read ? { swings: zz, read, channel: trendChannel(zz, read.trend) } : null,
			}, times);
			// The legend line changes only when the read does (not on every poll).
			setStructureRead(prev =>
				prev && read && prev.trend === read.trend && prev.broken === read.broken
					&& prev.pivot?.time === read.pivot?.time && prev.pivot?.price === read.pivot?.price ? prev : read);
			if (ema200Series) {
				ema200Vals = biasEma200Series(closed.map(c => c.close));
				ema200Series.setData(line(ema200Vals));
				setBias(check);
			}
			legendAt(hoverIdx ?? candles.length - 1);
			volumeSeries.setData(candles.map(c => ({
				time: c.time as UTCTimestamp,
				value: c.volume,
				color: c.close >= c.open ? chartColors.volumeUp : chartColors.volumeDown,
			})));
			volumeAvgSeries.setData(line(priorVolumeAverage(candles.map(c => c.volume), VOL_AVG_N)));

			// RSI and the legend's readout come from the candles the engine grades:
			// 1h at and below 1h (the chart's own above), so they match the desk.
			const src = fromHourly ? hourBars : candles;
			const srcTf = fromHourly ? 3600 : tfSec;
			const rsiSrc = rsiSeries(src.map(b => b.close), RSI_PERIOD);
			rsiLine?.setData(line(fromHourly ? stepToBars(times, src.map(b => b.time), rsiSrc, 3600) : rsiSrc));
			const n = src.length;
			const nowSec = replayClock ?? Date.now() / 1000;
			const forming = n > 0 && src[n - 1]!.time + srcTf > nowSec; // the last bar is still open
			const closedIdx = forming ? n - 2 : n - 1;
			const avg = priorVolumeAverage(src.map(b => b.volume), VOL_AVG_N);
			const ratioAt = (i: number) => (i >= 0 && avg[i] ? src[i]!.volume / avg[i]! : null);
			setReadout(closedIdx < 0 ? null : {
				rsiClosed: rsiSrc[closedIdx] ?? null,
				rsiNow: forming ? rsiSrc[n - 1] ?? null : null,
				volClosed: ratioAt(closedIdx),
				// The forming bar's volume projected to the full bar, as the desk's "on pace for".
				volPace: forming && avg[n - 1] ? pacedVolume(src[n - 1]!.volume, src[n - 1]!.time, srcTf, nowSec) / avg[n - 1]! : null,
			});
		};
		const publishBars = () =>
			setBars({
				key: `${symbol}:${timeframe}`,
				data: candles.map(c => ({ time: c.time, high: c.high, low: c.low })),
				lastClose: candles.at(-1)?.close ?? null,
			});
		// Decimals from the coin's price (the default 2 turned every level of
		// a 0.006 coin into "0.01"): same rule as the desk and the chart tags.
		const applyPrecision = (lastClose: number | undefined) => {
			if (!lastClose) return;
			const precision = pricePrecision(lastClose);
			const priceFormat = { type: "price" as const, precision, minMove: 10 ** -precision };
			for (const s of [candleSeries, ema20Series, ema50Series]) s.applyOptions({ priceFormat });
		};

		const load = async () => {
			try {
				const [main, hours] = await Promise.all([
					fetchCandles(timeframe),
					fromHourly ? fetchCandles("1h").catch(() => [] as Candle[]) : Promise.resolve([] as Candle[]),
				]);
				// Symbol/timeframe changed while fetching: this chart is gone.
				if (cancelled) return;
				candles = main;
				hourBars = hours;
				hourFetched = hourNow();
				applyPrecision(candles.at(-1)?.close);
				candleSeries.setData(candles.map(toBar));
				drawIndicators();
				publishBars();
				// Start on recent price action (readable candles); scroll/zoom for history.
				// Leave ~12% empty on the right: price-line titles sit there and
				// would cover the newest candles and signals. A plan's box needs more.
				const shown = INITIAL_BARS[timeframe] ?? 120;
				const n = candles.length;
				const room = hasPlanRef.current ? 0.3 : 0.12;
				if (n > shown) chart.timeScale().setVisibleLogicalRange({ from: n - shown, to: n + Math.round(shown * room) });
				else chart.timeScale().fitContent();
				setLoading(false);
			} catch {
				if (cancelled) return;
				setError("Connection Failed");
				setLoading(false);
			}
		};

		// Live: move the forming candle, add new ones, step the 1h EMAs after each 1h close.
		const poll = async () => {
			if (polling || document.hidden || candles.length === 0) return;
			polling = true;
			try {
				const { updates, newBar } = freshBars(candles, await fetchCandles(timeframe));
				if (cancelled || updates.length === 0) return;
				for (const c of updates) candleSeries.update(toBar(c));
				candles = [...candles.filter(c => c.time < updates[0]!.time), ...updates];
				if (fromHourly && hourNow() !== hourFetched) {
					hourBars = await fetchCandles("1h").catch(() => hourBars);
					hourFetched = hourNow();
					if (cancelled) return;
				}
				drawIndicators();
				if (newBar) publishBars();
			} catch {
				// Keep the chart as it is; the next poll tries again.
			} finally {
				polling = false;
			}
		};

		// Replay: a past window (closed candles only), shown up to the replay time.
		let replayAll: Candle[] = [];
		let replayHours: Candle[] = [];
		const fetchRange = async (tf: string, from: number, to: number): Promise<Candle[]> => {
			const res = await fetch(`/api/candles/${symbol}/${tf}?from=${Math.floor(from)}&to=${Math.floor(to)}`);
			const json = await res.json();
			if (!json.success || !Array.isArray(json.data)) throw new Error(json.error || "No candles");
			return json.data;
		};
		/** Show the candles that had closed by `nowSec` (one more: just append it). */
		const reveal = (nowSec: number, initial = false) => {
			if (replayAll.length === 0) return;
			const prev = candles;
			const next = visibleAt(replayAll, nowSec, tfSec);
			replayClock = nowSec;
			candles = next;
			hourBars = fromHourly ? visibleAt(replayHours, nowSec, 3600) : [];
			const oneMore = !initial && prev.length > 0 && next.length === prev.length + 1 && next[prev.length - 1]!.time === prev.at(-1)!.time;
			if (oneMore) candleSeries.update(toBar(next.at(-1)!));
			else candleSeries.setData(next.map(toBar));
			drawIndicators();
			publishBars();
		};
		const loadReplay = async (center: number) => {
			try {
				const from = center - REPLAY_BEFORE_BARS * tfSec;
				const to = center + replayAfterBars(tfSec) * tfSec;
				const [main, hours] = await Promise.all([
					fetchRange(timeframe, from, to),
					// The 1h EMA 50 and RSI need hours before the window too.
					fromHourly ? fetchRange("1h", from - 300 * 3600, to) : Promise.resolve([] as Candle[]),
				]);
				if (cancelled) return;
				if (main.length === 0) throw new Error("No candles for that date");
				replayAll = main;
				replayHours = hours;
				applyPrecision(main.at(-1)?.close);
				const live = replayRef.current;
				reveal(live?.mode === "active" ? live.nowSec : center, true);
				// More room on the right than live: the next candles appear there.
				const shown = INITIAL_BARS[timeframe] ?? 120;
				const n = candles.length;
				chart.timeScale().setVisibleLogicalRange({ from: n - shown, to: n + Math.round(shown * 0.35) });
				const last = main.at(-1)!;
				setReplayEnd({ sec: last.time + tfSec, today: last.time + 2 * tfSec > Date.now() / 1000 });
				setLoading(false);
			} catch {
				if (cancelled) return;
				setError("Couldn't load past candles");
				setLoading(false);
			}
		};
		replayCtlRef.current = rp ? { reveal: t => reveal(t), candles: () => candles } : null;

		if (rp) loadReplay(rp.nowSec);
		else load();
		const pollId = rp ? 0 : setInterval(poll, LIVE_POLL_MS);

		const container = chartContainerRef.current;
		const handleResize = () => {
			chart.applyOptions({ width: container.clientWidth, height: container.clientHeight });
			if (showRsi) requestAnimationFrame(measureRsiPane);
		};

		// Grid columns resize without a window resize (panels collapsing), so
		// observe the container as well.
		window.addEventListener("resize", handleResize);
		const observer = new ResizeObserver(handleResize);
		observer.observe(container);

		return () => {
			cancelled = true;
			clearInterval(pollId);
			window.removeEventListener("resize", handleResize);
			observer.disconnect();
			cancelAnimationFrame(rsiFrame);
			rsiPaneObserver?.disconnect();
			priceLinesRef.current = [];
			candleSeriesRef.current = null;
			overlayRef.current = null;
			structRef.current = null;
			rsiBandRef.current = null;
			manager.destroy();
			managerRef.current = null;
			setSelectedDrawingIds([]);
			replayCtlRef.current = null;
			seriesRef.current = null;
			chart.remove();
		};
	}, [symbol, timeframe, theme, showRsi, replayAnchor]); // theme: canvas colours are resolved once per chart

	// ── Replay: reveal, play, settle practice trades, keep the record ──────
	useEffect(() => {
		if (replayNow !== null) replayCtlRef.current?.reveal(replayNow);
	}, [replayNow]);
	const replayAtEnd = replayNow !== null && replayEnd !== null && replayNow >= replayEnd.sec;
	useEffect(() => {
		if (replayAtEnd) setReplayPlaying(false);
		if (!replayPlaying || replayAtEnd) return;
		const id = setInterval(() => setReplay(advance), 1000 / replaySpeed);
		return () => clearInterval(id);
	}, [replayPlaying, replaySpeed, replayAtEnd, timeframe]);
	const replayLastClose = bars?.key === `${symbol}:${timeframe}` ? bars.lastClose : null;
	// Each newly shown candle can hit an open practice trade's stop or target.
	useEffect(() => {
		if (replayNow === null) return;
		const shown = replayCtlRef.current?.candles() ?? [];
		setPractice(list => {
			const next = list.map(t => settle(t, shown));
			return next.some((t, i) => t !== list[i]) ? next : list;
		});
	}, [replayNow, bars]);
	const recordedRef = useRef(new Set<string>());
	useEffect(() => {
		const fresh = practice.filter(t => t.exit && !recordedRef.current.has(t.id));
		if (fresh.length === 0) return;
		for (const t of fresh) recordedRef.current.add(t.id);
		setPracticeRecord(addToRecord(fresh));
	}, [practice]);
	// Escape while choosing the start cancels it (and keeps full screen open).
	useEffect(() => {
		if (replay?.mode !== "picking") return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== "Escape" || e.defaultPrevented) return;
			e.preventDefault();
			setReplay(null);
		};
		window.addEventListener("keydown", onKey, true);
		return () => window.removeEventListener("keydown", onKey, true);
	}, [replay?.mode]);
	useEffect(() => {
		const s = seriesRef.current;
		if (!s) return;
		for (const l of s.lines) l.applyOptions({ visible: showEma });
		for (const v of s.vol) v.applyOptions({ visible: showVol });
		structRef.current?.prim.setVisible(structureLayers(timeframe === "4h", { sr: showSr, bias: showBias, structure: showStructure }));
		structRef.current?.ema200?.applyOptions({ visible: showBias });
		}, [showEma, showVol, showSr, showBias, showStructure, timeframe]);
	const practiceDrawn = useMemo(() => practice.map(t => practiceSignal(t, replayLastClose)), [practice, replayLastClose]);
	const openPractice = practice.find(t => !t.exit) ?? null;
	const practiceCard = useMemo(() => scorecard(practice), [practice]);

	// Keep the drawing layer in step with the store and the rail.
	useEffect(() => {
		managerRef.current?.setDrawings(shownDrawings);
	}, [shownDrawings]);
	useEffect(() => {
		managerRef.current?.setTool(drawTool);
	}, [drawTool]);
	useEffect(() => {
		managerRef.current?.setMagnet(magnet);
	}, [magnet]);
	useEffect(() => {
		managerRef.current?.setRiskUsd(riskUsd);
	}, [riskUsd]);
	// Tools disarm when the rail goes away (hidden, or leaving full screen on a short chart).
	useEffect(() => {
		if (!railOn) setDrawTool("none");
	}, [railOn]);
	const selectedDrawings = useMemo(
		() => shownDrawings.filter(d => selectedDrawingIds.includes(d.id)),
		[shownDrawings, selectedDrawingIds],
	);

	// Key levels by default (yesterday's high/low, week open, VWAP); the rest
	// on request. The choice is remembered.
	const [allLevels, setAllLevels] = useState<boolean>(() => {
		try { return localStorage.getItem(LEVELS_PREF) === "all"; } catch { return false; }
	});
	const toggleAllLevels = () => {
		setAllLevels(v => {
			try { localStorage.setItem(LEVELS_PREF, v ? "key" : "all"); } catch { /* ignore */ }
			return !v;
		});
	};
	// Levels are today's (prior day, week open, VWAP): none in a replay.
	const shownLevels = useMemo(
		() => (replayActive ? [] : (levels ?? []).filter(l => allLevels || l.major !== false)),
		[levels, allLevels, replayActive],
	);

	// Horizontal reference levels. Declared after the chart effect so the
	// candle series exists; re-applied whenever the chart is rebuilt.
	useEffect(() => {
		const series = candleSeriesRef.current;
		if (!series || shownLevels.length === 0) return;
		const lines = shownLevels.map((l) =>
			series.createPriceLine({
				price: l.price,
				color: themeColor(l.color, "#94a3b8"),
				lineWidth: 1,
				lineStyle: l.dashed ? LineStyle.Dashed : LineStyle.Solid,
				// Normal size: only key levels get a price-scale label, the rest
				// are plain lines. Expanded: everything is labelled.
				axisLabelVisible: expanded || l.major !== false,
				title: !compactAxis && (expanded || l.major !== false) ? l.title : "",
			})
		);
		priceLinesRef.current = lines;
		return () => {
			// The chart effect may already have removed the chart (symbol/timeframe change).
			if (candleSeriesRef.current !== series) return;
			for (const line of lines) series.removePriceLine(line);
			priceLinesRef.current = [];
		};
	}, [shownLevels, symbol, timeframe, theme, expanded, compactAxis]);

	// The EMA names on the price axis: off on a phone (the legend names them),
	// re-applied after every chart rebuild.
	useEffect(() => {
		const [e20, e50] = seriesRef.current?.lines ?? [];
		e20?.applyOptions({ title: compactAxis ? "" : "EMA 20" });
		e50?.applyOptions({ title: compactAxis ? "" : "EMA 50" });
		structRef.current?.ema200?.applyOptions({ title: compactAxis ? "" : "EMA 200" });
	}, [compactAxis, symbol, timeframe, theme, showRsi, replayAnchor]);

	// ── Signals ────────────────────────────────────────────────────────────
	// deskKey stands in for deskCheck, which callers rebuild on every render.
	const deskKey = deskCheck ? `${deskCheck.closeSec}:${deskCheck.plan?.action ?? "none"}:${deskCheck.plan?.entry ?? ""}` : "";
	const signals = useMemo(() => {
		if (!showSignals || !signalQuery.data) return [];
		// Replay: each signal only once replay time reaches it, its result only once it ended.
		if (replayNow !== null) {
			return signalQuery.data.signals
				.map(s => replaySignalAt(s, replayNow, replayLastClose))
				.filter((s): s is ChartSignal => s !== null);
		}
		return withDeskCheck(signalQuery.data.signals, deskCheck);
	}, [showSignals, signalQuery.data, deskKey, replayNow, replayLastClose]);
	const chartBars = bars?.key === `${symbol}:${timeframe}` ? bars.data : null;
	const firstBar = chartBars?.[0]?.time ?? null;
	// Signals inside the loaded candles, oldest first: the legend counts them, the arrows step through them.
	const inView = useMemo(() => (firstBar === null ? [] : signals.filter(s => s.signalTime >= firstBar)), [signals, firstBar]);
	const summary = useMemo(() => summarize(inView, firstBar ?? 0), [inView, firstBar]);
	const days = firstBar === null || !signalQuery.data
		? 0
		: Math.max(1, Math.min(signalQuery.data.days, Math.round(((replayNow ?? Date.now() / 1000) - firstBar) / 86400)));
	// The zone the coin is waiting for goes with the signals (same switch).
	// setupKey stands in for setup, which callers rebuild on every render.
	const setupKey = showSignals && setup && !replayActive
		? `${setup.direction}:${setup.low}:${setup.high}:${setup.stop}:${setup.fromSec}:${setup.armed}:${setup.plan?.tp1}:${setup.plan?.tp2}`
		: "";
	// Selected: a signal, or the zone (its card).
	// Everything drawn: the engine's signals, then the trader's own trades.
	// In a replay: the practice trades instead (the real ones are today's).
	const mine = replayActive ? practiceDrawn : myTrades;
	const drawn = useMemo(() => (mine.length > 0 ? [...signals, ...mine] : signals), [signals, mine]);
	const selectedId = selected && (drawn.some(s => s.id === selected.id) || (selected.id === SETUP_ID && setupKey !== ""))
		? selected.id
		: null;
	// No signal cards popping up under the pointer while placing a drawing.
	const pointerHover = drawTool === "none" ? hover : null;
	// The hovered signal, else the selected one, gets price tags; with neither,
	// the latest is drawn stronger. Line titles own the pane's right edge:
	// "EMA 20" always, "Week open" and friends when levels are on.
	const tagsId = pointerHover?.id ?? selectedId;
	const emphasisId = tagsId ?? inView.at(-1)?.id ?? null;
	const rightLane = shownLevels.length > 0 ? 76 : 52;

	useEffect(() => {
		overlayRef.current?.setData(chartBars ? drawn : [], chartBars ?? [], TF_SEC[timeframe] ?? 3600);
	}, [drawn, chartBars, timeframe, theme]);

	useEffect(() => {
		overlayRef.current?.setFocus({ emphasis: emphasisId, tags: tagsId, fit: selectedId });
	}, [emphasisId, tagsId, selectedId, chartBars, theme]);

	// Price tags keep out from under the legend (it grows with the signal summary).
	const legendRef = useRef<HTMLDivElement>(null);
	const [legendBox, setLegendBox] = useState<Rect | null>(null);
	useEffect(() => {
		const el = legendRef.current;
		if (!el) return;
		const measure = () => setLegendBox({ x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight });
		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	useEffect(() => {
		overlayRef.current?.setInsets({ rightLane, legend: legendBox });
		structRef.current?.prim.setInsets({ rightLane, legend: legendBox });
		managerRef.current?.setRightLane(rightLane);
	}, [rightLane, legendBox, chartBars, theme]);

	useEffect(() => {
		overlayRef.current?.setSetup(setupKey ? setup ?? null : null);
	}, [setupKey, chartBars, theme]);

	// The RSI band follows the setup's side: 35–60 for a buy, 40–65 for a sell.
	useEffect(() => {
		const colors = rsiBandColors.current;
		if (!colors) return;
		rsiBandRef.current?.setBand({ ...rsiBandOf(setup?.direction), ...colors });
	}, [setupKey, showRsi, theme]);

	useEffect(() => setSelected(null), [symbol]);
	// A replay belongs to one coin.
	useEffect(() => exitReplay(), [symbol]);

	const selectedIdx = selectedId ? inView.findIndex(s => s.id === selectedId) : -1;
	/** Select the older/newer signal (the latest first) and scroll it into view. */
	const step = (dir: -1 | 1) => {
		if (inView.length === 0) return;
		const next = selectedIdx === -1 ? inView.length - 1 : Math.min(inView.length - 1, Math.max(0, selectedIdx + dir));
		const sig = inView[next]!;
		setSelected({ id: sig.id, x: null });
		const chart = chartRef.current;
		const span = overlayRef.current?.spanOf(sig.id);
		const range = chart?.timeScale().getVisibleLogicalRange();
		if (!chart || !span || !range) return;
		// Keep the current zoom unless the trade is wider than the view.
		const width = Math.max(range.to - range.from, span.to - span.from + 16);
		const mid = (span.from + span.to) / 2;
		chart.timeScale().setVisibleLogicalRange({ from: mid - width / 2, to: mid + width / 2 });
	};

	// Card: the hovered signal or zone, else the selected one. Big charts float it
	// on the side away from it; small ones (drawer, phone) list the selected one below.
	const roomy = expanded || (height >= 320 && (chartContainerRef.current?.clientWidth ?? 0) >= 560);
	const cardId = roomy ? pointerHover?.id ?? selectedId : selectedId;
	const card = cardId && cardId !== SETUP_ID ? drawn.find(s => s.id === cardId) ?? null : null;
	// The zone's card is tall: it floats only over a tall chart (full screen);
	// otherwise it opens below the chart when the zone is clicked.
	const setupFloats = expanded || height >= 560;
	const setupCard = (setupFloats ? cardId : selectedId) === SETUP_ID && setupKey !== "" ? setup ?? null : null;
	const cardX = pointerHover && pointerHover.id === cardId ? pointerHover.x : selected?.x ?? null;
	const cardOnRight = cardX !== null && cardX < (chartContainerRef.current?.clientWidth ?? 0) / 2;
	const scaleWidth = chartRef.current?.priceScale("right").width() ?? 64;
	// A floating card fits between the legend and the time axis: a taller one
	// scrolls inside itself instead of running off the top of the chart.
	const cardStyle: CSSProperties = {
		maxHeight: `calc(100% - ${Math.round((legendBox ? legendBox.y + legendBox.h : 0) + 8 + 36)}px)`,
		...(cardOnRight ? { right: scaleWidth + 12 } : {}),
	};

	// Levels are today's: the legend leaves them out of a replay.
	const levelCount = replayActive ? 0 : levels?.length ?? 0;
	const keyLevelCount = replayActive ? 0 : (levels ?? []).filter(l => l.major !== false).length;
	// Showing all on the normal-size chart: the extra lines have no labels.
	const unlabelled = expanded ? 0 : shownLevels.filter(l => l.major === false).length;
	// Which candles the EMAs are read from: the engine's 1h, at 1h and below.
	const emaTf = (TF_SEC[timeframe] ?? 3600) <= 3600 ? "1h" : timeframe;
	// The RSI and volume readout in the checklist's words. The checklist grades
	// 1h candles: on 4h / 1d the readout is the chart's own, plain.
	const graded = emaTf === "1h";
	const band = rsiBandOf(setup?.direction);
	const rsiClosed = readout?.rsiClosed ?? null;
	const rsiWord = rsiClosed === null || !graded ? ""
		: rsiClosed < band.low ? `below ${band.low}: the trend may be broken`
		: rsiClosed > band.high ? `above ${band.high}: already ran`
		: `reset (${band.low}–${band.high})`;
	const rsiTone = rsiClosed === null ? "" : !graded ? "text-foreground" : rsiClosed >= band.low && rsiClosed <= band.high ? "text-profit" : "text-warning";
	const volTone = (x: number) => (!graded ? "text-foreground" : x < 0.5 ? "text-loss" : x >= 1.2 ? "text-profit" : "text-foreground");
	const isH4 = timeframe === "4h";
	const emaHint = isH4
		? "EMA 20/50 of 4h candles: the trend the checklist's “4h bias” reads."
		: timeframe === "1d"
			? "EMA 20/50 of daily candles: the big-picture trend (the desk's checks don't use them)."
			: "EMA 20/50: the trend lines the engine reads.";

	const drawLabel = railOn ? "Hide drawing tools" : "Drawing tools: lines, Fibonacci, boxes, notes";
	const coin = symbol.replace("USDT", "");
	// Binance's indicator sheet: what's drawn on the price, and in the panes under it.
	const indicatorGroups: IndicatorGroup[] = [
		{
			title: "On the price",
			chips: [
				{ id: "ema", label: "EMA 20/50", on: showEma, onChange: setShowEma },
				...(onShowLevels
					? [{
						id: "levels", label: "Levels", on: !!showLevels, onChange: onShowLevels,
						disabledReason: replayActive ? "Levels are today's: off in a replay" : undefined,
					}]
					: []),
				...(onShowLevels && levelCount > keyLevelCount
					? [{ id: "all-levels", label: `All ${levelCount}`, on: allLevels, onChange: (on: boolean) => { if (on !== allLevels) toggleAllLevels(); } }]
					: []),
				...(onShowSignals ? [{ id: "signals", label: "Signals", on: showSignals, onChange: onShowSignals }] : []),
				{ id: "structure", label: "Structure", on: showStructure, onChange: setShowStructure },
				...(isH4
					? [
						{ id: "sr", label: "Support / resistance", on: showSr, onChange: setShowSr },
						{ id: "bias", label: "4h bias", on: showBias, onChange: setShowBias },
					]
					: []),
				],
				hint: `${emaHint} Levels: yesterday's high and low, the week open, VWAP. Signals: the 1h strategy's BUY/SELL with stop and target. Structure: the swing highs and lows (HH higher high, HL higher low, LH lower high, LL lower low; a swing counts once price turns 2.5 normal candles away from it), the trend they make, its channel, and the swing a close must not break.${isH4
					? " Support / resistance: prices where 4h candles turned (a high above the 5 candles before and 2 after, or a low below them), joined when within half an average 4h candle (ATR); a dot marks each turn, more touches draw a firmer line. 4h bias: the desk's trend check, with the EMA 200 and the swing highs / lows it reads (HH higher high, LH lower high, HL higher low, LL lower low)."
					: ""}`,
		},
		{
			title: "Below the price",
			chips: [
				{ id: "vol", label: "Volume", on: showVol, onChange: setShowVol },
				{ id: "rsi", label: "RSI 14", on: showRsi, onChange: (on: boolean) => { if (on !== showRsi) toggleRsi(); } },
			],
			hint: graded
				? "Volume against its 20-candle average (a trigger needs at least 0.5×, an A+ setup 1.2×). RSI: a 0–100 momentum gauge; the shaded band is a pullback's reset zone."
				: "Volume against its 20-candle average. RSI: a 0–100 momentum gauge. The desk grades both on 1h candles: switch to 1h to check a setup.",
		},
	];
	// EMAs, RSI and volume below 1h are read from 1h candles: say so.
	const srcTag = emaTf !== timeframe ? ` (${emaTf})` : "";
	const toneCls = "data-[tone=up]:text-profit data-[tone=down]:text-loss";

	return (
		<div
			className={cn(expanded && "fixed inset-0 z-50 flex flex-col bg-background p-2 sm:p-3")}
			role={expanded ? "dialog" : undefined}
			aria-modal={expanded || undefined}
			aria-label={expanded ? `${symbol} chart, full screen` : undefined}
		>
		<Panel
			flush
			className={cn(className, expanded && "flex min-h-0 flex-1 flex-col")}
			// Full screen: a column, so the chart fills what the toolbar and bottom bar leave.
			bodyClassName={expanded ? "flex min-h-0 flex-1 flex-col" : undefined}
		>
			{/* Toolbar (Binance): coin, timeframe tabs, then quiet icons. */}
			<div className="@container flex h-11 shrink-0 items-center gap-2 border-b border-separator pl-4 pr-2">
				{symbolControl ?? (
					<span className="flex items-baseline gap-1 pr-1">
						<span className="text-sm font-semibold">{coin}</span>
						<span className="text-xs text-muted">/USDT</span>
					</span>
				)}
				<div className="no-scrollbar min-w-0 overflow-x-auto">
					{timeframes && onTimeframe
						? <TimeframeTabs value={timeframe} options={timeframes} onChange={onTimeframe} />
						: <span className="num text-xs text-muted">{timeframe}</span>}
				</div>
				<div className="ms-auto flex shrink-0 items-center gap-0.5">
					{actions}
					<IndicatorsMenu groups={indicatorGroups} />
					<ToolbarIcon
						label={showStructure ? "Hide market structure" : "Market structure: swing highs and lows (HH, HL, LH, LL) and the trend channel"}
						active={showStructure}
						onPress={() => setShowStructure(!showStructure)}
					>
						<Waypoints className="size-4" />
					</ToolbarIcon>
					{canDraw && (
						<ToolbarIcon label={drawLabel} active={railOn} onPress={() => setRailOn(!railOn)}>
							<PenLine className="size-4" />
						</ToolbarIcon>
					)}
					{allowExpand && (
						<ToolbarIcon label={expanded ? "Exit full screen (Esc)" : "Full screen"} active={expanded} onPress={() => setExpanded(!expanded)}>
							{expanded ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
						</ToolbarIcon>
					)}
				</div>
			</div>
			<div ref={drawScopeRef} className={cn("flex", expanded && "min-h-0 flex-1")}>
			{railOn && (
				<DrawingRail
					tool={drawTool}
					onTool={armTool}
					magnet={magnet}
					onMagnet={on => {
						setMagnet(on);
						showToast(on ? "Magnet on: points snap to the nearest candle open, high, low or close" : "Magnet off: points go exactly where you click");
					}}
					hidden={drawingsHidden}
					onHidden={hide => {
						if (hide) setDrawTool("none");
						setDrawingsHidden(hide);
						showToast(hide ? "Drawings hidden" : "Drawings shown");
					}}
					canUndo={canUndo}
					canRedo={canRedo}
					onUndo={() => drawingStore.undo(symbol)}
					onRedo={() => drawingStore.redo(symbol)}
					count={drawings.length}
					onClear={() => {
						const n = drawings.length;
						drawingStore.clear(symbol);
						showToast(`Deleted ${n} drawing${n === 1 ? "" : "s"}`, () => drawingStore.undo(symbol));
					}}
				/>
			)}
			<div className={cn("@container relative min-w-0 flex-1", expanded && "h-full")}>
				{/* Chart container: fixed height normally, fills the window when expanded
				    (the ResizeObserver resizes the canvas either way). */}
				<div ref={chartContainerRef} className="w-full" style={{ height: expanded ? "100%" : height }} />

				{/* Legend (Binance): plain coloured text on the chart, no box. Line 1 follows the crosshair. */}
				<div
					ref={legendRef}
					className="pointer-events-none absolute left-3 top-2 z-10 flex max-w-[calc(100%-5rem)] flex-col gap-0.5 text-2xs text-muted [text-shadow:0_0_3px_var(--surface),0_0_8px_var(--surface)]"
				>
					<div className="num flex flex-wrap items-center gap-x-2">
						<span ref={el => { legendEls.current.time = el; }} className="text-foreground/70" />
						<span>O <span ref={el => { legendEls.current.o = el; }} className="text-foreground" /></span>
						<span>H <span ref={el => { legendEls.current.h = el; }} className="text-foreground" /></span>
						<span>L <span ref={el => { legendEls.current.l = el; }} className="text-foreground" /></span>
						<span>C <span ref={el => { legendEls.current.c = el; }} className={cn("text-foreground", toneCls)} /></span>
						<span ref={el => { legendEls.current.chg = el; }} className={toneCls} />
					</div>
					{(showEma || showVol || (isH4 && showBias)) && (
						<div className="flex flex-wrap items-center gap-x-3">
							{showEma && (
								<>
									<span><span className="text-chart-ema-fast">EMA 20{srcTag}</span> <span ref={el => { legendEls.current.ema20 = el; }} className="num text-foreground" /></span>
									<span><span className="text-chart-ema-slow">EMA 50{srcTag}</span> <span ref={el => { legendEls.current.ema50 = el; }} className="num text-foreground" /></span>
								</>
							)}
							{isH4 && showBias && (
								<span><span className="text-chart-ema-long">EMA 200</span> <span ref={el => { legendEls.current.ema200 = el; }} className="num text-foreground" /></span>
							)}
							{/* Volume against the engine's yardstick: the average of the 20 bars before it. */}
							{showVol && readout?.volClosed != null && (
								<span>
									Vol{srcTag} <span className={cn("num font-semibold", volTone(readout.volClosed))}>{readout.volClosed.toFixed(2)}×</span> avg
									{readout.volPace != null && (
										<> · now <span className={cn("num font-semibold", volTone(readout.volPace))}>{readout.volPace.toFixed(2)}×</span></>
									)}
								</span>
							)}
							{allLevels && unlabelled > 0 && <span>{unlabelled} levels unlabelled · labels in full screen</span>}
						</div>
					)}
					{showStructure && structureRead && <StructureLegendRow read={structureRead} timeframe={timeframe} />}
					{isH4 && showBias && bias && <BiasLegendRow check={bias} />}
					{showSignals && (
						<SignalLegend
							loading={signalQuery.loading}
							error={signalQuery.error}
							summary={summary}
							days={days}
							canOlder={inView.length > 0 && selectedIdx !== 0}
							canNewer={inView.length > 0 && (selectedIdx === -1 || selectedIdx < inView.length - 1)}
							onOlder={() => step(-1)}
							onNewer={() => step(1)}
						/>
					)}
					{setupKey !== "" && setup && roomy && (
						<SetupLegendRow
							z={setup}
							open={selectedId === SETUP_ID}
							onToggle={() => setSelected(s => (s?.id === SETUP_ID ? null : { id: SETUP_ID, x: null }))}
						/>
					)}
				</div>

				{/* The RSI pane's own label: the reading the pullback checklist grades, in words. */}
				{showRsi && rsiTop !== null && (
					<div
						className="pointer-events-none absolute left-3 z-10 flex max-w-[calc(100%-6rem)] items-center gap-1.5 text-2xs text-muted [text-shadow:0_0_3px_var(--surface),0_0_8px_var(--surface)]"
						style={{ top: rsiTop + 3 }}
					>
						<span className="min-w-0 truncate">
							<span className="text-warning">RSI 14{srcTag}</span>
							{rsiClosed !== null && (
								<>
									{" "}<span className={cn("num font-semibold", rsiTone)}>{rsiClosed.toFixed(1)}</span>
									{" "}<span className="text-muted/80">{rsiWord}</span>
								</>
							)}
							{readout?.rsiNow != null && <span className="text-muted/80"> · now <span className="num">{readout.rsiNow.toFixed(1)}</span></span>}
						</span>
					</div>
				)}

				{setupCard && setupFloats ? (
					<SetupCard
						z={setupCard}
						pinned={selectedId === SETUP_ID}
						onClose={() => setSelected(null)}
						className={cn("absolute bottom-9 z-20", !cardOnRight && "left-3")}
						style={cardStyle}
					/>
				) : card && roomy && (
					<SignalCard
						s={card}
						pinned={selectedId === card.id}
						onClose={() => setSelected(null)}
						className={cn("absolute bottom-9 z-20", !cardOnRight && "left-3")}
						style={cardStyle}
					/>
				)}

				{/* Drawings: what to click while a tool is armed, else the selected drawing's style bar. */}
				{/* A full-width row centres them (left-1/2 would cap them at half the chart's width). */}
				<div className="pointer-events-none absolute inset-x-3 bottom-9 z-30 flex justify-center">
					{drawTool !== "none" ? (
						<div role="status" className="rounded-full border border-border bg-surface/95 px-3 py-1 text-xs text-foreground shadow-lg">
							{placingHint(drawTool)} <span className="text-muted">· Esc to cancel</span>
						</div>
					) : (
						<DrawingStyleBar
							symbol={symbol}
							selected={selectedDrawings}
							editRequest={editRequest}
							className="pointer-events-auto"
						/>
					)}
				</div>

				{toast && (
					<div className="pointer-events-none absolute inset-x-3 top-1/3 z-30 flex justify-center" role="status">
						<div key={toast.n} className="pointer-events-auto flex max-w-sm items-center gap-3 rounded-lg bg-foreground/90 px-3 py-2 text-xs text-surface shadow-lg">
							{toast.text}
							{toast.undo && (
								<button
									type="button"
									className="font-semibold underline underline-offset-2 outline-none focus-visible:ring-2 focus-visible:ring-focus"
									onClick={() => { toast.undo!(); setToast(null); }}
								>
									Undo
								</button>
							)}
						</div>
					</div>
				)}

				{/* First load / symbol switch: skeleton shaped like the chart */}
				{loading && !error && (
					<div className="absolute inset-0 z-20 flex flex-col gap-3 bg-surface p-4" aria-busy="true" aria-label="Loading chart">
						<Skeleton className="h-3 w-40 rounded-md" />
						<Skeleton className="flex-1 rounded-lg" />
						<div className="flex gap-3">
							{Array.from({ length: 6 }, (_, i) => (
								<Skeleton key={i} className="h-2.5 flex-1 rounded-md" />
							))}
						</div>
					</div>
				)}

				{error && (
					<div className="absolute inset-x-4 top-12 z-20">
						<Alert status="danger">
							<Alert.Indicator />
							<Alert.Content>
								<Alert.Title>Chart unavailable</Alert.Title>
								<Alert.Description>
									{error} — could not load {symbol} {timeframe} candles.
								</Alert.Description>
							</Alert.Content>
						</Alert>
					</div>
				)}
			</div>
			</div>
			{!replay && (canDraw || onBuy) && (
				<LiveActionBar onReplay={canDraw ? () => setReplay({ mode: "picking" }) : undefined} onBuy={onBuy} onSell={onSell} />
			)}
			{replay && (
				<ReplayBar
					mode={replay.mode}
					nowSec={replayNow}
					loading={replayActive && loading}
					error={replayActive ? error : null}
					playing={replayPlaying}
					speed={replaySpeed}
					atEnd={replayAtEnd}
					reachedToday={replayEnd?.today ?? false}
					openTrade={openPractice}
					openR={openPractice ? practiceR(openPractice, replayLastClose) : null}
					card={practiceCard}
					record={practiceRecord}
					lastClose={replayLastClose}
					atrValue={replayActive ? atr(replayCtlRef.current?.candles() ?? []) : null}
					riskUsd={riskUsd}
					priceFmt={p => p.toFixed(pricePrecision(p))}
					onRandom={() => startReplay(randomReplayTime(Date.now() / 1000, tfSecNow))}
					onPickDate={sec => startReplay(alignToBar(sec, tfSecNow))}
					onCancel={exitReplay}
					onTogglePlay={() => setReplayPlaying(v => !v)}
					onStep={() => setReplay(advance)}
					onSpeed={setReplaySpeed}
					onPlace={(side, stop, target) => {
						if (replayNow === null || replayLastClose === null) return;
						setPractice(list => [...list, { id: crypto.randomUUID(), side, entry: replayLastClose, stop, target, openSec: replayNow, exit: null }]);
					}}
					onCloseTrade={() => {
						if (replayNow === null || replayLastClose === null) return;
						const exit = { sec: replayNow - tfSecNow, price: replayLastClose, reason: "manual" as const };
						setPractice(list => list.map(t => (t.exit ? t : { ...t, exit })));
					}}
					onContinue={() => setReplay(r => (
						r?.mode === "active" ? { ...r, anchorSec: r.nowSec, signals: signalWindow(r.nowSec, Date.now() / 1000) } : r
					))}
					onExit={exitReplay}
				/>
			)}
			{setupCard && !setupFloats && <SetupCard z={setupCard} wide pinned onClose={() => setSelected(null)} className="w-full max-w-none rounded-none border-x-0 border-b-0 shadow-none" />}
			{!roomy && card && <SignalCard s={card} pinned onClose={() => setSelected(null)} className="w-full max-w-none rounded-none border-x-0 border-b-0 shadow-none" />}
		</Panel>
		</div>
	);
}
